// Sound Sets: word cards face up. Find the group that shares a sound: a rhyme, a vowel
// sound, a blend, a syllable count, or two at once. Groups can be bigger than three.

import { h, clear } from '@kit/dom';
import type { GameModule, GameContext } from '@shell/types';
import { makeBoard, type Board } from '@content/words/sounds';

const BOARDS = 6;

function mount(host: HTMLElement, ctx: GameContext) {
  let n = 0;
  let level = ctx.currentLevel();
  let b: Board = makeBoard(level, ctx.rng);
  let sel = new Set<number>();
  let found: [number, number][] = [];       // homophone pairs found
  let whyStage = false;
  let wrongs = 0, hinted = false, locked = false;
  let attempts = 0, correct = 0, minLevel = level;
  let speaking = 0;
  const t0 = performance.now();
  let tProblem = performance.now();

  const head = h('div.row', { style: { justifyContent: 'space-between' } }, h('span.muted', null, 'Board ', h('strong', null, '1'), ` of ${BOARDS}`), h('button.btn.small.ghost', { onclick: sayThem }, 'Say them'));
  const prompt = h('p.center', { style: { fontWeight: '700', minHeight: '44px', padding: '2px 4px' } });
  const grid = h('div', { style: { display: 'grid', gap: '8px' } });
  const controls = h('div.stack', { style: { gap: '8px', marginTop: '6px' } });
  host.append(head, prompt, grid, controls);

  function setup() {
    level = ctx.currentLevel();
    minLevel = Math.min(minLevel, level);
    b = makeBoard(level, ctx.rng);
    sel = new Set(); found = []; whyStage = false;
    wrongs = 0; hinted = false; locked = false;
    tProblem = performance.now();
    (head.querySelector('strong') as HTMLElement).textContent = String(n + 1);
    prompt.textContent = b.prompt;
    grid.style.gridTemplateColumns = `repeat(${b.cols}, 1fr)`;
    render();
  }

  function render() {
    clear(grid); clear(controls);
    b.cards.forEach((w, i) => {
      const inPair = found.some(p => p.includes(i));
      const on = sel.has(i);
      grid.append(h('button.choice', {
        disabled: locked || inPair,
        onclick: () => tap(i),
        style: { minHeight: '56px', fontSize: w.length > 8 ? '17px' : '20px', textTransform: 'lowercase', background: inPair ? 'var(--good)' : on ? 'var(--words)' : '', color: inPair || on ? '#fff' : '', borderColor: on ? 'var(--words)' : '' },
      }, w));
    });
    if (whyStage && b.whyChoices) {
      controls.append(h('p.center.muted.small', null, `Right, "${b.cards[b.answer[0]]}" is the odd one. Why?`));
      for (const why of b.whyChoices) controls.append(h('button.btn.block', { onclick: () => answerWhy(why) }, why));
    } else if (b.kind !== 'rhyme' && b.kind !== 'odd' && b.kind !== 'homophone') {
      controls.append(h('button.btn.primary.block', { onclick: done, disabled: locked }, `Done (${sel.size} picked)`));
    } else if (b.kind === 'rhyme') {
      controls.append(h('p.center.muted.small', null, `${sel.size} of 3 picked. The board checks itself at three.`));
    } else if (b.kind === 'homophone') {
      controls.append(h('p.center.muted.small', null, `Pairs found: ${found.length} of 3`));
    }
  }

  function tap(i: number) {
    if (locked) return;
    ctx.sound('tick');
    if (b.kind === 'odd') { sel = new Set([i]); checkOdd(i); return; }
    if (sel.has(i)) sel.delete(i); else sel.add(i);
    if (b.kind === 'rhyme' && sel.size === 3) { render(); setTimeout(done, 150); return; }
    if (b.kind === 'homophone' && sel.size === 2) { checkPair(); return; }
    render();
  }

  function report(ok: boolean) {
    const ms = performance.now() - tProblem;
    attempts++; if (ok) correct++;
    ctx.report({ skill: b.kind === 'rhyme' || b.kind === 'homophone' ? 'word_play' : 'phonics_spelling', level, correct: ok, hintUsed: hinted, ms, problemId: `sounds:${b.kind}` });
  }

  function done() {
    if (locked) return;
    const want = new Set(b.answer);
    const extra = [...sel].filter(i => !want.has(i));
    const missing = [...want].filter(i => !sel.has(i));
    if (!extra.length && !missing.length) {
      locked = true; report(true); ctx.sound('ding');
      ctx.say(['That is the group!', 'You heard it!', 'Sharp ears!'][n % 3]);
      render(); next();
      return;
    }
    wrongs++; ctx.sound('buzz');
    if (wrongs >= 2) {
      locked = true; report(false);
      sel = new Set(b.answer);
      ctx.say(`Here is the group: ${b.answer.map(i => b.cards[i]).join(', ')}.`);
      render(); next(1800);
      return;
    }
    hinted = true;
    if (extra.length) ctx.say(b.explain(b.cards[extra[0]]) + ' Try again.');
    else ctx.say(`Good so far, but ${missing.length} more belong${missing.length === 1 ? 's' : ''} in the group. Listen for it.`);
    if (b.kind === 'rhyme') { sel = new Set(); render(); }
  }

  function checkOdd(i: number) {
    if (i === b.answer[0]) {
      ctx.sound('ding'); whyStage = true; render();
    } else {
      wrongs++; ctx.sound('buzz');
      if (wrongs >= 2) { locked = true; report(false); sel = new Set(b.answer); ctx.say(`The odd one was "${b.cards[b.answer[0]]}". ${b.why}`); render(); next(1800); }
      else { hinted = true; ctx.say(b.explain(b.cards[i]) + ' Say them out loud and listen.'); sel = new Set(); render(); }
    }
  }

  function answerWhy(why: string) {
    if (locked) return;
    locked = true;
    const ok = why === b.why;
    report(ok);
    ctx.sound(ok ? 'ding' : 'buzz');
    ctx.say(ok ? `Exactly. ${b.why}` : `Close, but the real reason: ${b.why}`);
    render(); next(ok ? 1100 : 1700);
  }

  function checkPair() {
    const [x, y] = [...sel];
    const ok = b.pairs!.some(([a, c]) => (a === x && c === y) || (a === y && c === x));
    if (ok) {
      found.push([x, y]); report(true); ctx.sound('ding');
      ctx.say(`"${b.cards[x]}" and "${b.cards[y]}" sound the same but mean different things.`);
      sel = new Set(); render();
      if (found.length === 3) { locked = true; next(); }
    } else {
      wrongs++; report(false); ctx.sound('buzz'); hinted = true;
      ctx.say(`"${b.cards[x]}" and "${b.cards[y]}" sound different. Say them slowly.`);
      sel = new Set(); render();
    }
  }

  function sayThem() {
    if (speaking) return;
    const words = b.cards.slice();
    speaking = words.length;
    words.forEach((w, i) => setTimeout(() => { ctx.speak(w); speaking--; }, i * 900));
  }

  function next(delay = 1000) {
    n++;
    setTimeout(() => {
      if (n >= BOARDS) {
        if (attempts === correct && minLevel >= 5) ctx.charm('charm.rhyme');
        ctx.end({ attempts, correct, perfect: attempts === correct, durationMs: performance.now() - t0 });
      } else setup();
    }, delay);
  }

  setup();
  return { destroy() { clear(host); } };
}

const mod: GameModule = {
  id: 'words.sound-sets',
  title: 'Sound Sets',
  blurb: 'Words face up. Find the ones that share a sound: a rhyme, a vowel, a blend, a beat.',
  category: 'words',
  skills: ['word_play', 'phonics_spelling'],
  levelRange: [1, 10],
  unlock: { stars: 8 },
  mount,
};
export default mod;
